import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaCalendar, FaClock, FaUser, FaPhone, FaVideo, FaHome, FaStethoscope, FaMapMarkerAlt, FaCheck, FaTimes, FaEdit, FaFileUpload, FaPlay, FaStop, FaFile, FaCheckCircle, FaTimesCircle, FaExclamationCircle } from 'react-icons/fa';
import { API_BASE_URL, AUTH_TOKEN_KEY } from '../../utils/constants';
import { formatDate } from '../../utils/utils';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './AppointmentDetailsScreen.css';

const AppointmentDetailsScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [solutionProvided, setSolutionProvided] = useState('');
  const [followUpRequired, setFollowUpRequired] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showFullText, setShowFullText] = useState({});

  useEffect(() => {
    fetchAppointmentDetails();
  }, [id]);

  const fetchAppointmentDetails = async () => {
    try {
      setLoading(true);
      const accessToken = localStorage.getItem(AUTH_TOKEN_KEY);
      
      const response = await fetch(`${API_BASE_URL}api/appointment/${id}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAppointment(data.appointment);
      } else {
        toast.error('Failed to fetch appointment details');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching appointment details:', error);
      toast.error('Failed to fetch appointment details');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getServiceIcon = (serviceType) => {
    switch (serviceType) {
      case 'Tele Consultation':
        return <FaPhone className="service-icon-appointment" />;
      case 'videoConsultation':
        return <FaVideo className="service-icon-appointment" />;
      case 'inClinic':
        return <FaStethoscope className="service-icon-appointment" />;
      case 'homeVisit':
        return <FaHome className="service-icon-appointment" />;
      default:
        return <FaStethoscope className="service-icon-appointment" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'scheduled': return '#f1c40f';
      case 'confirmed': return '#27ae60';
      case 'completed': return '#3498db';
      default: return '#e74c3c';
    }
  };

  const handleApproveAppointment = async () => {
    setActionLoading(true);
    try {
      const accessToken = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_BASE_URL}api/appointment/${appointment._id}/approve`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setAppointment({...appointment, isApprovedByDoctor: true});
        toast.success('Appointment approved successfully');
      } else {
        toast.error('Failed to approve appointment');
      }
    } catch (error) {
      console.error('Error approving appointment:', error);
      toast.error('Failed to approve appointment');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelAppointment = async () => {
    if (!cancellationReason) {
      toast.error('Please provide a cancellation reason');
      return;
    }

    setActionLoading(true);
    try {
      const accessToken = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_BASE_URL}api/appointment/${appointment._id}/reject`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          appointmentId: appointment._id,
          doctorId: appointment.doctorId || appointment.doctor?._id,
          patientId: appointment.patientId,
          reason: cancellationReason,
        }),
      });

      if (response.ok) {
        setAppointment({...appointment, isCancelled: true, appointmentCancelledReason: cancellationReason});
        setShowCancelModal(false);
        toast.success('Appointment cancelled successfully');
      } else {
        toast.error('Failed to cancel appointment');
      }
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast.error('Failed to cancel appointment');
    } finally {
      setActionLoading(false);
    }
  };

  const fetchAvailableTimeSlots = async (date) => {
    try {
      const accessToken = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_BASE_URL}api/doctor/available-slots?date=${date}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableTimeSlots(data.availableSlots || []);
      } else {
        setAvailableTimeSlots([]);
      }
    } catch (error) {
      console.error('Error fetching time slots:', error);
      setAvailableTimeSlots([]);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedTimeSlot('');
    if (date) {
      fetchAvailableTimeSlots(date);
    } else {
      setAvailableTimeSlots([]);
    }
  };

  const handleRescheduleAppointment = async () => {
    if (!selectedDate || !selectedTimeSlot || !rescheduleReason) {
      toast.error('Please fill all required fields');
      return;
    }

    setActionLoading(true);
    try {
      const accessToken = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_BASE_URL}api/appointment/${appointment._id}/rescheduleDoctor`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          appointmentId: appointment._id,
          doctorId: appointment.doctorId || appointment.doctor?._id,
          patientId: appointment.patientId,
          selectedDate: selectedDate,
          selectedTimeSlot: selectedTimeSlot,
          reason: rescheduleReason,
        }),
      });

      if (response.ok) {
        setAppointment({...appointment, selectedDate, selectedTimeSlot});
        setShowRescheduleModal(false);
        toast.success('Appointment rescheduled successfully');
      } else {
        toast.error('Failed to reschedule appointment');
      }
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      toast.error('Failed to reschedule appointment');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteAppointment = async () => {
    if (!solutionProvided || !followUpRequired) {
      toast.error('Please fill all required fields');
      return;
    }

    setActionLoading(true);
    try {
      const accessToken = localStorage.getItem(AUTH_TOKEN_KEY);
      const formData = new FormData();
      formData.append('appointmentId', appointment._id);
      formData.append('doctorId', appointment.doctorId || appointment.doctor?._id);
      formData.append('patientId', appointment.patientId);
      formData.append('solutionProvided', solutionProvided);
      formData.append('followUpRequired', followUpRequired);
      formData.append('additionalNotes', additionalNotes);

      if (uploadedFile) {
        formData.append('file', uploadedFile);
      }

      const response = await fetch(`${API_BASE_URL}api/appointment/${appointment._id}/complete`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (response.ok) {
        setAppointment({...appointment, isCompleted: true, solutionProvided, followUpRequired, additionalNotes});
        setShowCompleteModal(false);
        toast.success('Appointment completed successfully');
      } else {
        toast.error('Failed to complete appointment');
      }
    } catch (error) {
      console.error('Error completing appointment:', error);
      toast.error('Failed to complete appointment');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const getStatusIcon = () => {
    if (appointment.isCancelled) {
      return <FaTimesCircle className="status-icon cancelled" />;
    } else if (appointment.isCompleted) {
      return <FaCheckCircle className="status-icon completed" />;
    } else if (appointment.isApprovedByDoctor) {
      return <FaCheckCircle className="status-icon approved" />;
    } else {
      return <FaExclamationCircle className="status-icon pending" />;
    }
  };

  const getStatusText = () => {
    if (appointment.isCancelled) {
      return `Cancelled ${appointment.appointmentCancelledBy === appointment.doctorId ? 'by You' : 'by Patient'}`;
    } else if (appointment.isCompleted) {
      return 'Completed';
    } else if (appointment.isApprovedByDoctor) {
      return 'Approved';
    } else {
      return 'Pending Approval';
    }
  };

  const getGenderIcon = (gender) => {
    if (gender === 'Male') {
      return '👨';
    } else if (gender === 'Female') {
      return '👩';
    }
    return '👤';
  };

  if (loading) {
    return <LoadingSpinner message="Loading appointment details..." />;
  }

  if (!appointment) {
    return (
      <div className="appointment-details-screen">
        <div className="error-state">
          <h3>Appointment not found</h3>
          <button onClick={() => navigate('/dashboard')} className="back-btn">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <DashboardLayout title="Appointment Details" showBackButton={true}>
      <div className="appointment-details-screen">

      <div className="appointment-card">
        <div className="status-section">
          {getStatusIcon()}
          <span className="status-text">{getStatusText()}</span>
          
          {!appointment.isApprovedByDoctor && !appointment.isCancelled && !appointment.isCompleted && (
            <div className="approval-actions">
              <button 
                className="approve-btn"
                onClick={handleApproveAppointment}
                disabled={actionLoading}
              >
                <FaCheck /> {actionLoading ? 'Approving...' : 'Approve'}
              </button>
            </div>
          )}
        </div>

        {appointment.isApprovedByDoctor && !appointment.isCompleted && !appointment.isCancelled && (
          <div className="service-actions-appointment">
            <div className="service-description">
              {appointment.serviceType === 'videoConsultation' && (
                <p>This is a video consultation. Please start the video call to connect with the client.</p>
              )}
              {(appointment.serviceType === 'teleConsultation' || appointment.serviceType === 'Tele Consultation') && (
                <p>This is a telephonic consultation. Please start the audio call to connect with the client.</p>
              )}
              {appointment.serviceType === 'chatConsultation' && (
                <p>This is a chat consultation. Please start the chat conversation to assist the client.</p>
              )}
              {appointment.serviceType === 'homeVisit' && (
                <p>This is a home visit service. Please use the direction button to navigate to the client's location.</p>
              )}
            </div>
            
            <div className="call-buttons">
              {appointment.serviceType === 'videoConsultation' && (
                <button className="call-btn video-btn">
                  <FaVideo /> Start Video Call
                </button>
              )}
              {(appointment.serviceType === 'teleConsultation' || appointment.serviceType === 'Tele Consultation') && (
                <button className="call-btn audio-btn">
                  <FaPhone /> Start Audio Call
                </button>
              )}
              {appointment.serviceType === 'chatConsultation' && (
                <button className="call-btn chat-btn">
                  <FaUser /> Start Chat
                </button>
              )}
              {appointment.serviceType === 'homeVisit' && (
                <button className="call-btn direction-btn">
                  <FaMapMarkerAlt /> Get Directions
                </button>
              )}
            </div>
          </div>
        )}

        <div className="patient-section">
          <div className="patient-avatar">
            {getGenderIcon(appointment.patientInfo.gender)}
          </div>
          <div className="patient-info">
            <h3>{appointment.patientInfo.name}</h3>
            <p>{appointment.patientInfo.gender} • Age: {appointment.patientInfo.age || 'N/A'}</p>
            {appointment.patientInfo.mobile && (
              <p><FaPhone /> {appointment.patientInfo.mobile}</p>
            )}
          </div>
        </div>

        <div className="appointment-info">
          <h4>Appointment Details</h4>
          <div className="info-grid">
            <div className="info-item">
              <FaUser className="info-icon" />
              <div>
                <label>ID</label>
                <span>{appointment.appointmentId || appointment._id}</span>
              </div>
            </div>
            <div className="info-item">
              <FaCalendar className="info-icon" />
              <div>
                <label>Date</label>
                <span>{formatDate(appointment.selectedDate)}</span>
              </div>
            </div>
            <div className="info-item">
              <FaClock className="info-icon" />
              <div>
                <label>Time</label>
                <span>{appointment.selectedTimeSlot}</span>
              </div>
            </div>
            <div className="info-item">
              {getServiceIcon(appointment.serviceType)}
              <div>
                <label>Service Type</label>
                <span>{appointment.serviceType}</span>
              </div>
            </div>
            <div className="info-item">
              <span className="fee-icon">₹</span>
              <div>
                <label>Consultation Fee</label>
                <span>₹{appointment.fees}</span>
              </div>
            </div>
            <div className="info-item">
              <FaUser className="info-icon" />
              <div>
                <label>Payment Method</label>
                <span>{appointment.paymentMethod || 'N/A'}</span>
              </div>
            </div>
            <div className="info-item">
              <FaCheckCircle className="info-icon" />
              <div>
                <label>Payment Status</label>
                <span>{appointment.paymentStatus || 'N/A'}</span>
              </div>
            </div>
            {appointment.isCancelled && (
              <div className="info-item">
                <FaTimesCircle className="info-icon" />
                <div>
                  <label>Cancellation Reason</label>
                  <span>{appointment.appointmentCancelledReason}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="patient-details">
          <h4>Patient Information</h4>
          <div className="info-grid">
            <div className="info-item">
              <FaUser className="info-icon" />
              <div>
                <label>Name</label>
                <span>{appointment.patientInfo.name}</span>
              </div>
            </div>
            <div className="info-item">
              <FaUser className="info-icon" />
              <div>
                <label>Gender</label>
                <span>{appointment.patientInfo.gender}</span>
              </div>
            </div>
            <div className="info-item">
              <FaCalendar className="info-icon" />
              <div>
                <label>Date of Birth</label>
                <span>{appointment.patientInfo.dateOfBirth || 'N/A'}</span>
              </div>
            </div>
            <div className="info-item full-width">
              <FaFile className="info-icon" />
              <div>
                <label>Problem Description</label>
                <span>{appointment.patientInfo.problem || appointment.symptoms}</span>
              </div>
            </div>
            {appointment.patientInfo.problemAudio && appointment.patientInfo.problemAudio.secure_url && (
              <div className="info-item full-width">
                <FaPlay className="info-icon" />
                <div>
                  <label>Problem Audio</label>
                  <audio controls className="audio-player">
                    <source src={appointment.patientInfo.problemAudio.secure_url} type="audio/mpeg" />
                    Your browser does not support the audio element.
                  </audio>
                </div>
              </div>
            )}
          </div>
        </div>

        {appointment.appointmentCompletionDetails && appointment.isCompleted && (
          <div className="solution-section">
            <h4>Solution by Expert</h4>
            <div className="info-grid">
              <div className="info-item">
                <FaCheckCircle className="info-icon" />
                <div>
                  <label>Follow-Up Required</label>
                  <span>{appointment.appointmentCompletionDetails.followUpRequired}</span>
                </div>
              </div>
              {appointment.appointmentCompletionDetails.followUpDate && (
                <div className="info-item">
                  <FaCalendar className="info-icon" />
                  <div>
                    <label>Follow-Up Date</label>
                    <span>{appointment.appointmentCompletionDetails.followUpDate}</span>
                  </div>
                </div>
              )}
              <div className="info-item full-width">
                <FaFile className="info-icon" />
                <div>
                  <label>Additional Notes</label>
                  <div className="expandable-text">
                    <span>
                      {showFullText.notes 
                        ? appointment.appointmentCompletionDetails.additionalNotes
                        : `${appointment.appointmentCompletionDetails.additionalNotes?.slice(0, 100)}${appointment.appointmentCompletionDetails.additionalNotes?.length > 100 ? '...' : ''}`
                      }
                    </span>
                    {appointment.appointmentCompletionDetails.additionalNotes?.length > 100 && (
                      <button 
                        className="view-more-btn"
                        onClick={() => setShowFullText({...showFullText, notes: !showFullText.notes})}
                      >
                        {showFullText.notes ? 'View Less' : 'View More'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="info-item full-width">
                <FaFile className="info-icon" />
                <div>
                  <label>Solution Provided</label>
                  <div className="expandable-text">
                    <span>
                      {showFullText.solution 
                        ? appointment.appointmentCompletionDetails.solutionProvided
                        : `${appointment.appointmentCompletionDetails.solutionProvided?.slice(0, 100)}${appointment.appointmentCompletionDetails.solutionProvided?.length > 100 ? '...' : ''}`
                      }
                    </span>
                    {appointment.appointmentCompletionDetails.solutionProvided?.length > 100 && (
                      <button 
                        className="view-more-btn"
                        onClick={() => setShowFullText({...showFullText, solution: !showFullText.solution})}
                      >
                        {showFullText.solution ? 'View Less' : 'View More'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {appointment.appointmentCompletionDetails.document && (
                <div className="info-item full-width">
                  <FaFile className="info-icon" />
                  <div>
                    <label>Uploaded Document</label>
                    <a 
                      href={appointment.appointmentCompletionDetails.document.secure_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="document-link"
                    >
                      View Document
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {appointment.address && (
          <div className="address-section">
            <h4><FaMapMarkerAlt /> Address</h4>
            <p>{appointment.address}</p>
          </div>
        )}

        <div className="actions-section">
          {/* Show "Mark As Complete" if the appointment is approved */}
          {appointment.isApprovedByDoctor && !appointment.isCompleted && !appointment.isCancelled && (
            <button 
              className="action-btn complete"
              onClick={() => setShowCompleteModal(true)}
            >
              <FaCheckCircle /> Mark as Complete
            </button>
          )}
          
          {/* Show "Cancel" and "Reschedule" if the appointment is not completed, not canceled, and not approved */}
          {!appointment.isCompleted && !appointment.isCancelled && !appointment.isApprovedByDoctor && (
            <>
              <button 
                className="action-btn cancel"
                onClick={() => setShowCancelModal(true)}
              >
                <FaTimes /> Cancel
              </button>
              <button 
                className="action-btn reschedule"
                onClick={() => setShowRescheduleModal(true)}
              >
                <FaEdit /> Reschedule
              </button>
            </>
          )}
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Cancel Appointment</h3>
              <button onClick={() => setShowCancelModal(false)} className="close-btn">
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to cancel this appointment? Please provide a reason.</p>
              <div className="reason-options">
                {['Not feeling well', 'Change in schedule', 'Other'].map(reason => (
                  <button
                    key={reason}
                    className={`reason-btn ${cancellationReason === reason ? 'selected' : ''}`}
                    onClick={() => setCancellationReason(reason)}
                  >
                    {reason}
                  </button>
                ))}
              </div>
              {cancellationReason === 'Other' && (
                <textarea
                  placeholder="Enter your reason"
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  className="reason-input"
                />
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="confirm-btn cancel"
                onClick={handleCancelAppointment}
                disabled={actionLoading || !cancellationReason}
              >
                {actionLoading ? 'Cancelling...' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Reschedule Appointment</h3>
              <button onClick={() => setShowRescheduleModal(false)} className="close-btn">
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Select Date</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                  className="date-input"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              {selectedDate && (
                <div className="form-group">
                  <label>Select Time Slot</label>
                  {availableTimeSlots.length > 0 ? (
                    <div className="time-slots-grid">
                      {availableTimeSlots.map((slot, index) => (
                        <button
                          key={index}
                          type="button"
                          className={`time-slot-btn ${selectedTimeSlot === slot ? 'selected' : ''}`}
                          onClick={() => setSelectedTimeSlot(slot)}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="no-slots">No available time slots for this date</p>
                  )}
                </div>
              )}
              <div className="form-group">
                <label>Reason for Rescheduling</label>
                <div className="reason-options">
                  {['Doctor unavailable', 'Change in schedule', 'Other'].map(reason => (
                    <button
                      key={reason}
                      className={`reason-btn ${rescheduleReason === reason ? 'selected' : ''}`}
                      onClick={() => setRescheduleReason(reason)}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
                {rescheduleReason === 'Other' && (
                  <textarea
                    placeholder="Enter your reason"
                    value={rescheduleReason}
                    onChange={(e) => setRescheduleReason(e.target.value)}
                    className="reason-input"
                  />
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="confirm-btn reschedule"
                onClick={handleRescheduleAppointment}
                disabled={actionLoading || !selectedDate || !selectedTimeSlot || !rescheduleReason}
              >
                {actionLoading ? 'Rescheduling...' : 'Confirm Reschedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Modal */}
      {showCompleteModal && (
        <div className="modal-overlay">
          <div className="modal large">
            <div className="modal-header">
              <h3>Complete Appointment</h3>
              <button onClick={() => setShowCompleteModal(false)} className="close-btn">
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Solution Provided *</label>
                <textarea
                  placeholder="Enter the solution provided to the patient"
                  value={solutionProvided}
                  onChange={(e) => setSolutionProvided(e.target.value)}
                  className="solution-input"
                  rows="4"
                  required
                />
              </div>
              <div className="form-group">
                <label>Follow-Up Required? *</label>
                <div className="followup-options">
                  <button
                    className={`followup-btn ${followUpRequired === 'Yes' ? 'selected' : ''}`}
                    onClick={() => setFollowUpRequired('Yes')}
                  >
                    Yes
                  </button>
                  <button
                    className={`followup-btn ${followUpRequired === 'No' ? 'selected' : ''}`}
                    onClick={() => setFollowUpRequired('No')}
                  >
                    No
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Additional Notes</label>
                <textarea
                  placeholder="Enter any additional notes (optional)"
                  value={additionalNotes}
                  onChange={(e) => setAdditionalNotes(e.target.value)}
                  className="notes-input"
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Upload File (Optional)</label>
                <input
                  type="file"
                  onChange={handleFileUpload}
                  className="file-input"
                />
                {uploadedFile && (
                  <div className="uploaded-file">
                    <FaFile /> {uploadedFile.name}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="confirm-btn complete"
                onClick={handleCompleteAppointment}
                disabled={actionLoading || !solutionProvided || !followUpRequired}
              >
                {actionLoading ? 'Completing...' : 'Confirm Completion'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </DashboardLayout>
  );
};

export default AppointmentDetailsScreen;